import { Alert, Container, Spinner } from 'react-bootstrap';
import { type CSSProperties, Fragment, useEffect, useMemo, useState } from 'react';
import './Dashboard.css';
import TasksPanel from '../components/tasks/TasksPanel';
import { dashboardRoles, normalizeDashboardRole } from '../config/dashboardRoleConfig';
import { useApi } from '../utils/useApi';

type Segment = {
  label: string;
  value: number;
  color: string;
};

type HeatStatus = 'good' | 'watch' | 'risk';

type KpiCard = {
  title: string;
  value: string;
  note: string;
  tone: string;
  color?: string;
};

type BarItem = {
  label: string;
  value: number;
  color?: string;
};

type HeatmapRow = {
  area: string;
  statuses: HeatStatus[];
};

type TimelineItem = {
  label: string;
  start: number;
  end: number;
  state: string;
};

type TimelineRow = {
  name: string;
  items: TimelineItem[];
};

type ClusterItem = {
  name: string;
  color: string | null;
  dashboard_role?: string | null;
};

type DocumentEntry = {
  id?: string | number;
  current?: boolean;
  legal_area?: string;
  legal_area_color?: string;
  law_id?: number | null;
  law_name?: string;
  law_part_id?: number | null;
  client_law_part_id?: number | null;
  clusters?: ClusterItem[];
};

type TaskCategory = {
  id: number;
  name: string;
};

type TaskEntry = {
  id: number;
  note?: string;
  suggestion?: string;
  deadline?: string | null;
  done?: number | boolean;
  origin?: string | number | null;
  client_law_part_id?: string | number | null;
  evidence_required?: boolean | number;
  evidence_provided?: boolean | number;
  deadlineDate?: Date | null;
  task_categories?: TaskCategory[];
};

type DashboardData = {
  kpis: KpiCard[];
  complianceStatus: Segment[];
  legalAreas: Segment[];
  clusterBars: BarItem[];
  clusterDonut: Segment[];
  recurringChecks: BarItem[];
  evidenceRequired: BarItem[];
  heatmap: { columns: string[]; rows: HeatmapRow[] };
  inspectionObjects: BarItem[];
  inspectionStatus: Segment[];
  nextInspections: { asset: string; date: string; status: string }[];
  timeline: { months: string[]; rows: TimelineRow[] };
  changeRadar: ChangeRadarEntry[];
};

type ChangeRadarEntry = {
  id: number;
  anstehende_aenderungen: string;
  materie: string;
  legal_area_id?: number | null;
  legal_area_name?: string | null;
  legal_area_color?: string | null;
  bewertungsstatus: string;
  status_neu: string;
};

const DASHBOARD_TEXT = {
  headerTitle: 'Dashboard',
  headerSubtitle: 'Kompakte Sicht auf Vorschriften, Maßnahmen, Prüfunqgen und Nachweise.',
  kpiTitles: ['Zugeordnete Vorschriften', 'Rechtsgebiete im Scope', 'Cluster', 'Prüfpflichten'],
  complianceStatusTitle: 'Compliance Status',
  legalAreasTitle: 'Rechtsgebiete',
  clusterTitle: 'Cluster',
  recurringChecksTitle: 'Anzahl der Maßnahmen bei zugewiesenen wiederkehrenden Prüfungen',
  evidenceRequiredTitle: 'Nachweise erforderlich',
  tabs: {
    dashboard: 'Compliance Control',
    tasks: 'Maßnahmen',
    inspections: 'Prüfübersicht',
    changeRadar: 'Änderungs Radar'
  },
  heatmapTitle: 'Heatmap Rechtsmaterien',
  inspectionsTitle: 'Anzahl prüfpflichtige Objekte',
  inspectionStatusTitle: 'Prüfstatus',
  upcomingInspectionsTitle: 'Nächste Prüfungen',
  timelineTitle: 'Prüf- und Maßnahmenplan'
};

const statusClassMap: Record<string, string> = {
  Fällig: 'watch',
  OK: 'good',
  Überfällig: 'risk',
  'In Prüfung': 'watch'
};

const palette = ['#2aa9e0', '#43b86c', '#f3b335', '#e45a5a', '#6a87f0', '#00b7a8', '#d27cf8'];

const parseDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};


const toPercent = (value: number, total: number) => {
  if (total <= 0) return 0;
  return Math.round((value / total) * 100);
};

const getHeatStatus = (ratio: number): HeatStatus => {
  if (ratio >= 0.66) return 'good';
  if (ratio >= 0.35) return 'watch';
  return 'risk';
};

const donutStyle = (segments: Segment[]) => {
  const total = segments.reduce((sum, segment) => sum + Math.max(0, segment.value), 0);
  if (total <= 0) return { background: '#d9e2ec' };

  const gradientSteps: string[] = [];
  let cursor = 0;

  segments.forEach((segment) => {
    const percent = (Math.max(0, segment.value) / total) * 100;
    const start = cursor;
    const end = cursor + percent;
    gradientSteps.push(`${segment.color} ${start}% ${end}%`);
    cursor = end;
  });

  return {
    background: `conic-gradient(${gradientSteps.join(', ')})`
  };
};

const normalizeDonutSegments = (segments: Segment[]): Segment[] => {
  const total = segments.reduce((sum, segment) => sum + Math.max(0, segment.value), 0);
  if (total <= 0) {
    return [{ label: 'Keine Daten', value: 100, color: '#d9e2ec' }];
  }
  return segments;
};

const toSoftTint = (color?: string | null) => {
  if (!color) return undefined;

  const hex6 = color.match(/^#([0-9a-fA-F]{6})$/);
  if (hex6) return `${color}2e`;

  const hex3 = color.match(/^#([0-9a-fA-F]{3})$/);
  if (hex3) {
    const [r, g, b] = hex3[1].split('');
    return `#${r}${r}${g}${g}${b}${b}2e`;
  }

  return undefined;
};

const Dashboard = () => {
  const { apiFetch } = useApi();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'tasks' | 'inspections' | 'changeRadar'>('dashboard');

  const updateChangeRadarValue = async (
    entryId: number,
    field: 'bewertungsstatus' | 'status_neu',
    value: string
  ) => {
    try {
      await apiFetch(`/api/change-radar/${entryId}`, 'PATCH', { [field]: value });
      setData((previous) => {
        if (!previous) return previous;
        return {
          ...previous,
          changeRadar: previous.changeRadar.map((entry) =>
            entry.id === entryId ? { ...entry, [field]: value } : entry
          )
        };
      });
    } catch {
      setError('Änderungsradar konnte nicht aktualisiert werden.');
    }
  };

  useEffect(() => {
    let isMounted = true;

    Promise.all([
      apiFetch('/api/documents').catch(() => []),
      apiFetch('/api/tasks/0').catch(() => []),
      apiFetch('/api/change-radar').catch(() => []),
      apiFetch('/api/admin/clusters').catch(() => [])
    ])
      .then(([docsPayload, tasksPayload, changeRadarPayload, clusterPayload]) => {
        if (!isMounted) return;

        const documents = (Array.isArray(docsPayload) ? docsPayload : []) as DocumentEntry[];
        const tasks = (Array.isArray(tasksPayload) ? tasksPayload : []) as TaskEntry[];
        const changeRadar = (Array.isArray(changeRadarPayload)
          ? changeRadarPayload
          : []) as ChangeRadarEntry[];
        const dashboardRoleMap = new Map<string, Set<string>>();
        Object.values(dashboardRoles).forEach((role) => dashboardRoleMap.set(role, new Set()));

        (Array.isArray(clusterPayload) ? clusterPayload : []).forEach((cluster: any) => {
          const name = String(cluster.name || '').trim();
          if (!name) return;

          const rawRoleValue = cluster.dashboard_role === null || cluster.dashboard_role === undefined
            ? (cluster.dashboardRole === null || cluster.dashboardRole === undefined ? 'none' : cluster.dashboardRole)
            : cluster.dashboard_role;

          const role = normalizeDashboardRole(rawRoleValue);
          if (role === dashboardRoles.none) return;
          const selectedNames = dashboardRoleMap.get(role) || new Set();
          selectedNames.add(name);
          dashboardRoleMap.set(role, selectedNames);
        });

        const selectedDashboardNames = new Set(
          Array.from(dashboardRoleMap.values()).flatMap((set) => Array.from(set))
        );

        const regulationKey = (doc: DocumentEntry) => {
          if (doc.law_id != null && doc.law_id !== 0) {
            return `law:${doc.law_id}`;
          }
          const fallback = `${(doc.law_name || '').trim()}|${doc.client_law_part_id ?? doc.law_part_id ?? doc.id ?? 0}`;
          return fallback ? `name:${fallback}` : `doc:${doc.id ?? Math.random()}`;
        };

        const uniqueDocuments = new Map<string, DocumentEntry>();
        documents.forEach((doc) => {
          const key = regulationKey(doc);
          if (!uniqueDocuments.has(key)) {
            uniqueDocuments.set(key, doc);
          }
        });

        const uniqueDocs = Array.from(uniqueDocuments.values());

        const legalAreasMap = new Map<string, { count: number; color: string }>();
        const uniqueRegulationAreaAssignments = new Set<string>();

        documents.forEach((doc, index) => {
          const area = (doc.legal_area || 'Nicht zugeordnet').trim() || 'Nicht zugeordnet';
          const assignmentKey = `${regulationKey(doc)}|${area}`;
          if (uniqueRegulationAreaAssignments.has(assignmentKey)) return;

          uniqueRegulationAreaAssignments.add(assignmentKey);
          const color = doc.legal_area_color || palette[index % palette.length];
          const current = legalAreasMap.get(area) || { count: 0, color };
          current.count += 1;
          legalAreasMap.set(area, current);
        });

        const clusterMap = new Map<string, { label: string; color: string; value: number }>();

        uniqueDocs.forEach((doc) => {
          const uniqueClusters = new Map<string, ClusterItem>();
          (doc.clusters || []).forEach((cluster) => {
            const clusterName = (cluster.name || '').trim();
            if (!clusterName) return;
            const key = `${clusterName}|${(cluster.color || '').trim()}`;
            if (!uniqueClusters.has(key)) uniqueClusters.set(key, cluster);
          });

          uniqueClusters.forEach((cluster) => {
            const label = (cluster.name || 'Unbenannt').trim();
            const color = cluster.color || '#4f93d1';
            const key = `${label}|${color}`;
            const current = clusterMap.get(key) || { label, color, value: 0 };
            current.value += 1;
            clusterMap.set(key, current);
          });
        });

        const clusterItems = Array.from(clusterMap.values()).sort((a, b) => b.value - a.value);

        const docHasRole = (doc: DocumentEntry, role: string) =>
          (doc.clusters || []).some((cluster) => {
            const name = (cluster.name || '').trim();
            if (!name) return false;
            if (selectedDashboardNames.size > 0 && !selectedDashboardNames.has(name)) return false;
            return (dashboardRoleMap.get(role) || new Set()).has(name);
          });

        const totalRegulations = uniqueDocs.length;
        // const currentRegulations = uniqueDocs.filter((doc) => Boolean(doc.current)).length;
        const legalAreasInScope = legalAreasMap.size;
        const distinctClusterCount = clusterItems.length;

        const recurringDocs = uniqueDocs.filter((doc) => docHasRole(doc, dashboardRoles.recurring));
        const recurringDocCount = recurringDocs.length;
        const recurringLawPartIds = new Set<number>();
        recurringDocs.forEach((doc) => {
          if (doc.law_part_id != null) recurringLawPartIds.add(Number(doc.law_part_id));
          if (doc.client_law_part_id != null) recurringLawPartIds.add(Number(doc.client_law_part_id));
        });

        const normalizedTasks = tasks.map((task) => ({
          ...task,
          done: task.done === true || task.done === 1,
          deadlineDate: parseDate(task.deadline)
        }));

        const recurringTasks = normalizedTasks.filter((task) => {
          const origin = Number(task.origin ?? 0);
          const clientLawPartId = Number(task.client_law_part_id ?? 0);
          const fallbackLawPartId = Number(task.origin ?? 0);
          return (
            (origin > 0 && recurringLawPartIds.has(origin)) ||
            (clientLawPartId > 0 && recurringLawPartIds.has(clientLawPartId)) ||
            (fallbackLawPartId > 0 && recurringLawPartIds.has(fallbackLawPartId))
          );
        });
        const recurringCount = recurringTasks.length;

        const inReviewCount = uniqueDocs.filter((doc) => docHasRole(doc, dashboardRoles.inReview)).length;
        const currentComplianceCount = uniqueDocs.filter(
          (doc) =>
            !docHasRole(doc, dashboardRoles.inReview) &&
            !docHasRole(doc, dashboardRoles.overdue)
        ).length;

        const recurringExceeded = recurringTasks.filter((task) => {
          if (task.done) return false;
          if (!task.deadlineDate) return false;
          return task.deadlineDate.getTime() < new Date().getTime();
        }).length;
        const recurringInTime = Math.max(0, recurringCount - recurringExceeded);
        const recurringEvidenceRequired = recurringTasks.filter((task) => Boolean(task.evidence_required)).length;
        const recurringEvidenceProvided = recurringTasks.filter((task) => Boolean(task.evidence_provided)).length;

        const legalAreas: Segment[] = Array.from(legalAreasMap.entries())
          .map(([label, info]) => ({ label, value: info.count, color: info.color }))
          .sort((a, b) => b.value - a.value);

        const openTasks = normalizedTasks.filter((task) => !task.done);
        const now = new Date();

        const nextInspectionsFromTasks = openTasks
          .filter((task) => task.deadlineDate)
          .sort((a, b) => a.deadlineDate!.getTime() - b.deadlineDate!.getTime())
          .slice(0, 6)
          .map((task) => {
            const diff = Math.floor((task.deadlineDate!.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            const status = diff < 0 ? 'Überfällig' : diff <= 10 ? 'Fällig' : 'OK';
            return {
              asset: task.task_categories?.[0]?.name || task.note || `Maßnahme ${task.id}`,
              date: task.deadlineDate!.toLocaleDateString('de-DE'),
              status
            };
          });

        const nextInspectionsFallback = recurringDocs.slice(0, 6).map((doc, index) => ({
          asset: doc.law_name || `Vorschrift ${index + 1}`,
          date: '-',
          status: docHasRole(doc, dashboardRoles.overdue)
            ? 'Überfällig'
            : 'In Prüfung'
        }));

        const nextInspections =
          nextInspectionsFromTasks.length > 0 ? nextInspectionsFromTasks : nextInspectionsFallback;

        const timelineMonths = Array.from({ length: 6 }, (_, index) => {
          const date = new Date(now.getFullYear(), now.getMonth() + index, 1);
          return date.toLocaleDateString('de-DE', { month: 'short' });
        });

        const timelineMap = new Map<string, TimelineItem[]>();

        openTasks
          .filter((task) => task.deadlineDate)
          .sort((a, b) => a.deadlineDate!.getTime() - b.deadlineDate!.getTime())
          .slice(0, 10)
          .forEach((task) => {
            const row = task.task_categories?.[0]?.name || 'Maßnahmen';
            const offset =
              (task.deadlineDate!.getFullYear() - now.getFullYear()) * 12 +
              (task.deadlineDate!.getMonth() - now.getMonth());
            const start = Math.max(0, Math.min(5, offset));
            const end = Math.min(6, start + 1);
            const diff = Math.floor((task.deadlineDate!.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            const state = diff < 0 ? 'risk' : diff <= 20 ? 'watch' : 'action';
            const label = (task.note || task.suggestion || `Maßnahme ${task.id}`).trim().slice(0, 36);

            const rowItems = timelineMap.get(row) || [];
            rowItems.push({ label, start, end, state });
            timelineMap.set(row, rowItems);
          });

        const timelineRows: TimelineRow[] = Array.from(timelineMap.entries()).map(([name, items]) => ({
          name,
          items
        }));

        const areaHeatRows = Array.from(legalAreasMap.entries())
          .map(([area, info]) => {
            const docs = documents.filter((doc) => (doc.legal_area || 'Nicht zugeordnet').trim() === area);
            const total = docs.length || 1;
            const currentRatio = docs.filter((doc) => Boolean(doc.current)).length / total;
            const recurringRatio = docs.filter((doc) => docHasRole(doc, dashboardRoles.recurring)).length / total;
            const overdueRatio = docs.filter((doc) => docHasRole(doc, dashboardRoles.overdue)).length / total;
            return {
              area,
              score: info.count,
              statuses: [
                getHeatStatus(currentRatio),
                getHeatStatus(recurringRatio),
                getHeatStatus(1 - overdueRatio),
                getHeatStatus(0)
              ] as HeatStatus[]
            };
          })
          .sort((a, b) => b.score - a.score)
          .slice(0, 8)
          .map(({ area, statuses }) => ({ area, statuses }));

        const inspectionObjects: BarItem[] = Array.from(legalAreasMap.entries())
          .map(([label, info]) => {
            const value = documents.filter(
              (doc) =>
                (doc.legal_area || 'Nicht zugeordnet').trim() === label &&
                docHasRole(doc, dashboardRoles.recurring)
            ).length;
            return { label, value, color: info.color };
          })
          .filter((item) => item.value > 0)
          .sort((a, b) => b.value - a.value)
          .slice(0, 6);

        setData({
          kpis: [
            {
              title: DASHBOARD_TEXT.kpiTitles[0],
              value: String(totalRegulations),
              note: `Zugewiesene Vorschriften abgeglichen mit aktueller Veröffentlichung: ${currentComplianceCount}`,
              tone: 'good'
            },
            {
              title: DASHBOARD_TEXT.kpiTitles[1],
              value: String(legalAreasInScope),
              note: 'Abgedeckte Themenbereiche',
              tone: 'watch',
              color: 'MediumOrchid'
            },
            {
              title: DASHBOARD_TEXT.kpiTitles[2],
              value: String(distinctClusterCount),
              note: 'Verschiedene Cluster in Vorschriften',
              tone: 'risk'
            },
            {
              title: DASHBOARD_TEXT.kpiTitles[3],
              value: String(recurringDocCount),
              note: 'wiederkehrende Prüfungen',
              tone: 'info'
            }
          ],
          complianceStatus: normalizeDonutSegments([
            { label: 'Aktuell', value: currentComplianceCount, color: '#25b86a' },
            { label: 'Zur Aktualitätsprüfung zugewiesen', value: inReviewCount, color: '#f5b82e' },
            { label: 'Aktualitätsprüfung überfällig', value: uniqueDocs.filter((doc) => docHasRole(doc, dashboardRoles.overdue)).length, color: '#e55959' }
          ]),
          legalAreas: normalizeDonutSegments(legalAreas),
          clusterBars: clusterItems.map((item) => ({
            label: item.label,
            value: item.value,
            color: item.color
          })),
          clusterDonut: normalizeDonutSegments(
            clusterItems.map((item) => ({
              label: item.label,
              value: item.value,
              color: item.color
            }))
          ),
          recurringChecks: [
            { label: 'In Zeit', value: toPercent(recurringInTime, recurringCount), color: '#25b86a' },
            { label: 'Überfällig', value: toPercent(recurringExceeded, recurringCount), color: '#ff3030' }
          ],
          evidenceRequired: [
            {
              label: 'Nachweise erforderlich',
              value: recurringEvidenceRequired,
              color: '#cd8500'
            },
            {
              label: 'Nachweise erbracht',
              value: recurringEvidenceProvided,
              color: '#2e8b57'
            }
          ],
          heatmap: {
            columns: ['Vorschriftenstand', 'Prüfstand', 'Überfälligkeit'],
            rows: areaHeatRows
          },
          inspectionObjects,
          inspectionStatus: normalizeDonutSegments([
            { label: 'In Zeit', value: recurringInTime, color: '#25b86a' },
            { label: 'Fällig', value: Math.max(0, recurringCount - recurringInTime - recurringExceeded), color: '#f5b82e' },
            { label: 'Überfällig', value: recurringExceeded, color: '#e55959' }
          ]),
          nextInspections,
          timeline: {
            months: timelineMonths,
            rows: timelineRows
          },
          changeRadar
        });
      })
      .catch(() => {
        if (!isMounted) return;
        setError('Dashboard-Daten konnten nicht geladen werden.');
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [apiFetch]);

  const topClusterBars = useMemo(() => data?.clusterBars || [], [data]);
  const inspectionObjects = useMemo(() => data?.inspectionObjects || [], [data]);

  const maxClusterValue = useMemo(
    () => Math.max(1, ...topClusterBars.map((item) => item.value)),
    [topClusterBars]
  );

  const maxInspectionValue = useMemo(
    () => Math.max(1, ...inspectionObjects.map((item) => item.value)),
    [inspectionObjects]
  );

  const monthLabels = useMemo(() => data?.timeline.months || [], [data]);

  const legalAreaLegendCount = useMemo(() => {
    if (!data) return 0;
    return data.legalAreas.filter((segment) => segment.label !== 'Keine Daten').length;
  }, [data]);

  const legalAreasLegendColumns = useMemo(() => {
    if (legalAreaLegendCount >= 16) return 4;
    if (legalAreaLegendCount >= 8) return 3;
    if (legalAreaLegendCount >= 5) return 2;
    return 1;
  }, [legalAreaLegendCount]);

  const legalAreasLegendGroups = useMemo(() => {
    if (!data) return [] as Segment[][];

    const entries = data.legalAreas;
    const columnCount = Math.max(1, legalAreasLegendColumns);
    const baseSize = Math.floor(entries.length / columnCount);
    const remainder = entries.length % columnCount;

    let cursor = 0;
    const groups: Segment[][] = [];

    for (let index = 0; index < columnCount; index += 1) {
      const currentSize = baseSize + (index < remainder ? 1 : 0);
      const slice = entries.slice(cursor, cursor + currentSize);
      if (slice.length > 0) groups.push(slice);
      cursor += currentSize;
    }

    return groups;
  }, [data, legalAreasLegendColumns]);

  const donutPanelMainGridStyle = useMemo(() => {
    if (legalAreasLegendColumns > 2) {
      return {
        '--compliance-fr': '0.52',
        '--legal-fr': '1.9',
        '--compliance-donut-size': '142px',
        '--compliance-min': '240px',
        '--legal-min': '700px'
      } as CSSProperties;
    }

    if (legalAreaLegendCount >= 8) {
      return {
        '--compliance-fr': '0.78',
        '--legal-fr': '1.45',
        '--compliance-donut-size': '162px',
        '--compliance-min': '300px',
        '--legal-min': '560px'
      } as CSSProperties;
    }

    return {
      '--compliance-fr': '1.08',
      '--legal-fr': '1.12',
      '--compliance-donut-size': '190px',
      '--compliance-min': '360px',
      '--legal-min': '420px'
    } as CSSProperties;
  }, [legalAreaLegendCount, legalAreasLegendColumns]);

  if (loading) {
    return (
      <Container className="dashboard-loading mt-4">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (!data) {
    return (
      <Container className="dashboard-page mt-4">
        <Alert variant="warning">Dashboard-Daten sind aktuell nicht verfügbar.</Alert>
      </Container>
    );
  }

  return (
    <Container className="dashboard-page mt-4">
      <div className="dashboard-shell">
        <header className="dashboard-header">
          <div>
            <h1>{DASHBOARD_TEXT.headerTitle}</h1>
            <p className="subtitle">{DASHBOARD_TEXT.headerSubtitle}</p>
          </div>
        </header>

        {error && <Alert variant="warning">{error}</Alert>}

        <section className="dashboard-tabs" aria-label="Dashboard-Reiter">
          <button
            type="button"
            className={activeTab === 'dashboard' ? 'active' : ''}
            onClick={() => setActiveTab('dashboard')}
          >
            {DASHBOARD_TEXT.tabs.dashboard}
          </button>
          <button
            type="button"
            className={activeTab === 'tasks' ? 'active' : ''}
            onClick={() => setActiveTab('tasks')}
          >
            {DASHBOARD_TEXT.tabs.tasks}
          </button>
          <button
            type="button"
            className={activeTab === 'inspections' ? 'active' : ''}
            onClick={() => setActiveTab('inspections')}
            style={{ display: 'none' }}
          >
            {DASHBOARD_TEXT.tabs.inspections}
          </button>
          <button
            type="button"
            className={activeTab === 'changeRadar' ? 'active' : ''}
            onClick={() => setActiveTab('changeRadar')}
          >
            {DASHBOARD_TEXT.tabs.changeRadar}
          </button>
        </section>

        {activeTab === 'tasks' && (
          <section className="panel">
            <TasksPanel law_part_id={0} hideId={true} showRegulationColumn={true} highlightRegulationColumnOnly={true} applyRowColorFromDeadline={false} showAddButton={false} />
          </section>
        )}

        {activeTab === 'dashboard' && (
          <>
            <section className="kpi-grid" aria-label="Kennzahlenübersicht">
              {data.kpis.map((card, index) => (
                <article
                  className={`kpi-card tone-${card.tone}`}
                  key={`${index}-${card.value}-${card.note}`}
                  style={card.color ? { '--kpi-color': card.color } as CSSProperties : undefined}
                >
                  <p>{card.title}</p>
                  <h2>{card.value}</h2>
                  <span>{card.note}</span>
                </article>
              ))}
            </section>

            <section className="panel-grid panel-grid-main two-columns donut-main-grid" style={donutPanelMainGridStyle}>
              <article className="panel donut-panel donut-panel-compliance">
                <h3>{DASHBOARD_TEXT.complianceStatusTitle}</h3>
                <div className="donut-wrap">
                  <div className="donut" style={donutStyle(data.complianceStatus)} aria-hidden="true" />
                  <ul className="donut-legend donut-legend-compliance">
                    {data.complianceStatus.map((segment) => (
                      <li key={segment.label}>
                        <span style={{ backgroundColor: segment.color }} />
                        {segment.label} {segment.value}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>

              <article className="panel donut-panel">
                <h3>{DASHBOARD_TEXT.legalAreasTitle}</h3>
                <div className="donut-wrap">
                  <div className="donut" style={donutStyle(data.legalAreas)} aria-hidden="true" />
                  <div
                    className="donut-legend-legal-grid"
                    style={{ '--legend-columns': String(legalAreasLegendColumns) } as CSSProperties}
                  >
                    {legalAreasLegendGroups.map((group, columnIndex) => (
                      <ul className="donut-legend donut-legend-legal" key={`legal-col-${columnIndex}`}>
                        {group.map((segment) => (
                          <li key={segment.label}>
                            <span style={{ backgroundColor: segment.color }} />
                            {segment.label} {segment.value}
                          </li>
                        ))}
                      </ul>
                    ))}
                  </div>
                </div>
              </article>
            </section>

            <section className="panel-grid panel-grid-main two-columns">
              <article className="panel obligations-panel">
                <h3>{DASHBOARD_TEXT.clusterTitle}</h3>
                <ul className="cluster-bars">
                  {topClusterBars.map((item) => (
                    <li key={item.label}>
                      <p className="cluster-label">{item.label}</p>
                      <div className="bar-track">
                        <div
                          className="bar-fill"
                          style={{
                            width: `${(item.value / maxClusterValue) * 100}%`,
                            background: item.color || '#d94848'
                          }}
                        />
                      </div>
                      <strong>{item.value}</strong>
                    </li>
                  ))}
                </ul>
              </article>

              <article className="panel donut-panel">
                <h3>{DASHBOARD_TEXT.clusterTitle}</h3>
                <div className="donut-wrap">
                  <div className="donut" style={donutStyle(data.clusterDonut)} aria-hidden="true" />
                  <ul>
                    {data.clusterDonut.map((segment) => (
                      <li key={segment.label}>
                        <span style={{ backgroundColor: segment.color }} />
                        {segment.label} {segment.value}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            </section>

            <section className="panel-grid recurring-panels">
              <article className="panel panel-compact">
                <h3>{DASHBOARD_TEXT.recurringChecksTitle}</h3>
                <ul className="percent-bars recurring-bars compact-bars">
                  {data.recurringChecks.map((item) => (
                    <li key={item.label}>
                      <p className="recurring-label">{item.label}</p>
                      <div className="bar-track">
                        <div
                          className="bar-fill secondary"
                          style={{ width: `${item.value}%`, background: item.color || '#1262b4' }}
                        />
                      </div>
                      <strong>{item.value}%</strong>
                    </li>
                  ))}
                </ul>
              </article>

              <article className="panel panel-compact">
                <h3>{DASHBOARD_TEXT.evidenceRequiredTitle}</h3>
                <ul className="percent-bars recurring-bars compact-bars">
                  {(() => {
                    const maxEvidenceValue = Math.max(1, ...data.evidenceRequired.map((item) => item.value));
                    return data.evidenceRequired.map((item) => (
                      <li key={item.label}>
                        <p className="recurring-label">{item.label}</p>
                        <div className="bar-track">
                          <div
                            className="bar-fill secondary"
                            style={{
                              width: `${(item.value / maxEvidenceValue) * 100}%`,
                              background: item.color || '#1262b4'
                            }}
                          />
                        </div>
                        <strong>{item.value}</strong>
                      </li>
                    ));
                  })()}
                </ul>
              </article>

            </section>
          </>
        )}

        {activeTab === 'inspections' && (
          <>
            <section className="panel heatmap-panel">
              <h3>{DASHBOARD_TEXT.heatmapTitle}</h3>
              <div
                className="heatmap-grid"
                role="table"
                aria-label="Heatmap matrix"
                style={{ '--heatmap-cols': String(data.heatmap.columns.length) } as CSSProperties}
              >
                <div className="heatmap-corner" role="columnheader" />
                {data.heatmap.columns.map((column) => (
                  <div className="heatmap-header" key={column} role="columnheader">
                    {column}
                  </div>
                ))}
                {data.heatmap.rows.map((row) => (
                  <Fragment key={row.area}>
                    <div className="heatmap-row-label" key={`${row.area}-label`} role="rowheader">
                      {row.area}
                    </div>
                    {row.statuses.map((status, index) => (
                      <div
                        className={`heat-cell ${status}`}
                        key={`${row.area}-${data.heatmap.columns[index]}-${status}`}
                        role="cell"
                      />
                    ))}
                  </Fragment>
                ))}
              </div>
            </section>

            <section className="panel-grid panel-grid-secondary">
              <article className="panel obligations-panel">
                <h3>{DASHBOARD_TEXT.inspectionsTitle}</h3>
                <ul>
                  {inspectionObjects.map((item) => (
                    <li key={item.label}>
                      <span>{item.label}</span>
                      <div className="bar-track">
                        <div
                          className="bar-fill secondary"
                          style={{
                            width: `${(item.value / maxInspectionValue) * 100}%`,
                            background: item.color || '#1262b4'
                          }}
                        />
                      </div>
                      <strong>{item.value}</strong>
                    </li>
                  ))}
                </ul>
              </article>

              <article className="panel donut-panel">
                <h3>{DASHBOARD_TEXT.inspectionStatusTitle}</h3>
                <div className="donut-wrap">
                  <div className="donut" style={donutStyle(data.inspectionStatus)} aria-hidden="true" />
                  <ul>
                    {data.inspectionStatus.map((segment) => (
                      <li key={segment.label}>
                        <span style={{ backgroundColor: segment.color }} />
                        {segment.label} {segment.value}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>

              <article className="panel obligations-table">
                <h3>{DASHBOARD_TEXT.upcomingInspectionsTitle}</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Objekt</th>
                      <th>Nächste Prüfung</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.nextInspections.map((item) => (
                      <tr key={`${item.asset}-${item.date}`}>
                        <td>{item.asset}</td>
                        <td>{item.date}</td>
                        <td className={`status ${statusClassMap[item.status] || 'watch'}`}>{item.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </article>
            </section>

            <section className="panel timeline-panel">
              <h3>{DASHBOARD_TEXT.timelineTitle}</h3>
              <div className="timeline-grid">
                <div className="timeline-left" />
                {monthLabels.map((month) => (
                  <div className="timeline-month" key={month}>
                    {month}
                  </div>
                ))}

                {data.timeline.rows.map((row) => (
                  <Fragment key={row.name}>
                    <div className="timeline-row-name" key={`${row.name}-name`}>
                      {row.name}
                    </div>
                    <div className="timeline-row-track" key={`${row.name}-track`}>
                      {row.items.map((item) => {
                        const left = (item.start / Math.max(1, monthLabels.length)) * 100;
                        const width = ((item.end - item.start) / Math.max(1, monthLabels.length)) * 100;

                        return (
                          <div
                            className={`timeline-pill ${item.state}`}
                            key={`${row.name}-${item.label}`}
                            style={{ left: `${left}%`, width: `${width}%` }}
                          >
                            {item.label}
                          </div>
                        );
                      })}
                    </div>
                  </Fragment>
                ))}

                {data.timeline.rows.length === 0 && (
                  <div className="timeline-empty">Keine anstehenden Einträge</div>
                )}
              </div>
            </section>
          </>
        )}

        {activeTab === 'changeRadar' && (
          <section className="panel obligations-table">
            <h3>Änderungsradar</h3>
            <table>
              <thead>
                <tr>
                  <th>Anstehende Änderungen</th>
                  <th>Rechtsgebiete</th>
                  <th>Bewertungsstatus</th>
                  <th>Status neu</th>
                </tr>
              </thead>
              <tbody>
                {data.changeRadar.map((entry) => {
                  const rowTint = toSoftTint(entry.legal_area_color);

                  return (
                    <tr
                      key={entry.id}
                      className={rowTint ? 'change-radar-row-colored' : undefined}
                      style={
                        rowTint
                          ? ({ '--change-radar-row-color': rowTint } as CSSProperties)
                          : undefined
                      }
                    >
                      <td
                        className={`change-radar-text-cell${(entry.anstehende_aenderungen || '').length > 151 ? ' change-radar-text-cell-long' : ''}`}
                        title={entry.anstehende_aenderungen}
                      >
                        <span className="change-radar-text-content">{entry.anstehende_aenderungen}</span>
                      </td>
                      <td>{entry.legal_area_name || entry.materie}</td>
                      <td>
                        <select
                          className="dashboard-select dashboard-select-compact"
                          value={entry.bewertungsstatus}
                          onChange={(event) =>
                            updateChangeRadarValue(entry.id, 'bewertungsstatus', event.target.value)
                          }
                        >
                          <option value="beobachten">beobachten</option>
                          <option value="fällig">fällig</option>
                          <option value="überfällig">überfällig</option>
                          <option value="bewertet">bewertet</option>
                        </select>
                      </td>
                      <td>
                        <select
                          className="dashboard-select dashboard-select-compact"
                          value={entry.status_neu}
                          onChange={(event) => updateChangeRadarValue(entry.id, 'status_neu', event.target.value)}
                        >
                          <option value="relevant">relevant</option>
                          <option value="nicht relevant">nicht relevant</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
                {data.changeRadar.length === 0 && (
                  <tr>
                    <td colSpan={4}>Keine Einträge vorhanden.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </Container>
  );
};

export default Dashboard;
